<template>
  <v-dialog
    v-model="open"
    eager
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card
      tile
      class="d-flex flex-column preview-card"
      @copy="preventCopy"
    >
      <v-toolbar
        class="flex-grow-0 z--1"
      >
        <v-toolbar-title>
          {{ title }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          v-if="flags['recommendation-aside'] && recommendedResourceHubArticles.length && $isDesktop"
          text
          class="primary-grey--text font-weight-medium"
          @click="nextRecommendedArticle"
        >
          NEXT RECOMMENDED ARTICLE
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          icon
          data-qa="close-preview-dialog"
          @click="closePreviewDialog"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-sheet
        v-if="$isDesktop && partnerCard"
        class="preview-card__partner-drawer"
        width="300"
      >
        <PartnerCardPreview
          :card="partnerCard"
          variant="side"
          interactive
          @cta-click="handlePartnerCtaClick"
        />
      </v-sheet>
      <div
        ref="wrapper"
        class="preview-card__wrapper d-flex flex-column"
        :style="{ overflowY: ($isMobile && showQnaPanelMobile) ? 'hidden' : 'auto' }"
      >
        <teaser
          :show="showTeaser"
          :title="title.split('|')[0].trim()"
        >
          <WidgetLayout
            v-if="$isDesktop"
            :widgets="widgets"
            shadowless
            no-toolbar
            :desktop-content-col-width="6"
          >
            <template #desktopContent>
              <interval-highlighter
                ref="highlighter"
                :apply="applyHighlights"
                :value="currentTime"
              >
                <div
                  ref="content"
                  class="preview-card__content d-flex flex-column flex-shrink-0"
                  @dragstart="preventDrag"
                  @click="contentClickHandler"
                  @auxclick="contentClickHandler"
                />
              </interval-highlighter>
            </template>
            <template #recommendationAside>
              <div class="pt-16 mt-16" />
              <div class="pt-8" />
              <div class="pt-16" />
              <RecommendationAside
                :recommendations="recommendedArticles"
                class="pa-4 rounded-lg"
                style="border: 1px solid #F5F5F5;"
                @view="previewFile($event)"
              />
            </template>
          </WidgetLayout>
          <div v-else>
            <interval-highlighter
              ref="highlighter"
              :apply="applyHighlights"
              :value="currentTime"
            >
              <div
                ref="content"
                class="preview-card__content d-flex flex-column flex-shrink-0"
                @dragstart="preventDrag"
                @click="contentClickHandler"
                @auxclick="contentClickHandler"
              />
            </interval-highlighter>
          </div>
        </teaser>
        <div
          ref="comments"
          class="flex-shrink-0"
        >
          <v-divider />
          <v-container class="py-12">
            <template
              v-if="isRatingEnabled === true"
            >
              <div class="d-flex flex-wrap align-center mb-4">
                <h2 class="text-h6">
                  Rating
                </h2>
                <v-rating
                  v-model="rating"
                  color="primary"
                  empty-icon="mdi-star-outline"
                  full-icon="mdi-star"
                  hover
                  length="5"
                  size="24"
                  value="0"
                />
              </div>
            </template>
          </v-container>
        </div>
      </div>
    </v-card>
    <div class="d-none">
      <AudioInfix ref="audioInfix" />
    </div>
    <div class="d-none">
      <RecommendationCarousel
        ref="recommendationCarousel"
        :recommendations="recommendedArticles"
        @view="previewFile($event)"
      />
    </div>
    <div
      v-if="!$isMobile && !scrollDetected && flags['sinoa-content-specific-questions']"
      class="mb-5 article-qna-chips-desktop"
    >
      <ArticleQnaChips
        v-if="!showTeaser"
        ref="articleQnaChips"
        :items="preGeneratedQna"
      />
    </div>
    <div
      v-if="$isMobile && showQnaPanelMobile"
      :class="{ 'article-qna-overlay': flags['sinoa-content-specific-questions'] }"
      @touchmove.prevent
      @wheel.prevent
    />
    <PartnerCardMobileFAB
      v-if="partnerCard && $isMobile"
      :card="partnerCard"
      @cta-click="handlePartnerCtaClick"
    />
    <FloatingAssistantBtn
      v-if="showFloatingAssistantBtn"
      :context-text="sinoaContextText"
      :highlight-text="$isMobile && showQnaPanelMobile"
      :delegate-click="sinoaIsDelegateClick"
      @click="handleFabClick"
    />
    <ArticleQnaChips
      v-if="showArticleQnaChips"
      ref="articleQnaChips"
      :items="preGeneratedQna"
      @close="handleCloseMobilePanel"
    />
  </v-dialog>
</template>

<script>
/* global libraryData */
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import { formatDate } from '../../helpers/formatting';
import Teaser from './Teaser.vue';
import AudioInfix from './AudioInfix.vue';
import IntervalHighlighter from './IntervalHighlighter.vue';
import { datadogLogs } from '@datadog/browser-logs';
import RecommendationCarousel from './RecommendationCarousel.vue';
import WidgetLayout from '../../pages/farm_profile/WidgetLayout.vue';
import RecommendationAside from './RecommendationAside.vue';
import FloatingAssistantBtn from '../chatbot/FloatingAssistantBtn.vue';
import ArticleQnaChips from '../chatbot/ArticleQnaChips.vue';
import PartnerCardPreview from '../marketing/PartnerCardPreview.vue';
import PartnerCardMobileFAB from '../marketing/PartnerCardMobileFAB.vue';
import { apiGetPartnerCardsByPlacement, apiRecordPartnerCardImpression, apiRecordPartnerCardCtaClick } from '../../api/marketingModule';
import { getPlacementForContent } from '../marketing/partnerCardConstants';
import { datadogRum } from '@datadog/browser-rum';

function readLibraryData() {
  try {
    return typeof libraryData !== 'undefined' ? libraryData : undefined;
  } catch {
    return undefined;
  }
}

async function getHH(m) {
  const e = new TextEncoder();
  const d = e.encode(m);
  const hb = await crypto.subtle.digest('SHA-256', d);
  const ha = Array.from(new Uint8Array(hb));
  const hh = ha.map((b) => (`00${b.toString(16)}`).slice(-2)).join('');
  return hh;
}

export default {
  components: {
    Teaser,
    AudioInfix,
    IntervalHighlighter,
    RecommendationCarousel,
    WidgetLayout,
    RecommendationAside,
    FloatingAssistantBtn,
    ArticleQnaChips,
    PartnerCardPreview,
    PartnerCardMobileFAB,
  },

  data() {
    return {
      open: false,
      id: 0,
      title: '',
      isLoading: false,
      shared: true,
      rating: 0,
      isRatingEnabled: false,
      audio_dict: null,
      showTeaser: false,
      html: '',
      contentUrl: '',
      debouncedLibraryViewUpdate: null,
      lastScrollTop: 0,
      scrollDetected: false,
      preGeneratedQna: [],
      showQnaPanelMobile: false,
      qnaDismissedMobile: false,
      partnerCard: null,
    };
  },

  computed: {
    ...mapState('launchDarkly', ['flags']),
    ...mapState('mediaPlayer', ['currentTime', 'playing']),
    ...mapGetters('mediaPlayer', ['activeTTSFile']),
    ...mapState('recommendations', ['mlAPIRecommendations', 'recommendedArticles']),
    ...mapState('user', ['isExternalUser']),
    ...mapState('permissions', ['permissions']),
    ...mapGetters('permissions', ['checkPermission']),
    ...mapState('chatbot', ['isGrainFoxAssistantDialogOpen', 'isChatUsageLoading']),
    ...mapGetters('subscription', ['userLocationCode']),

    applyHighlights() {
      return this.flags['text-to-speech-highlighting'] && !!this.activeTTSFile && this.playing;
    },

    showRecommendationCarousel() {
      return this.flags['recommendation-carousel'] && this.recommendedArticles.length && !this.$isDesktop;
    },

    widgets() {
      return this.flags['recommendation-aside'] && this.recommendedArticles.length ? [{ key: 'recommendationAside' }] : [];
    },

    recommendedResourceHubArticles() {
      return this.recommendedArticles.filter((article) => article.source === 'grainfox');
    },

    showFloatingAssistantBtn() {
      return this.flags['grainfox-ai'] && this.permissions && this.checkPermission('ai_chatbot', 'read')
        && !this.scrollDetected;
    },

    showArticleQnaChips() {
      return this.$isMobile
        && this.showQnaPanelMobile
        && this.preGeneratedQna.length > 0
        && this.flags['sinoa-content-specific-questions']
        && !this.showTeaser;
    },

    sinoaContextText() {
      return this.$isMobile && this.showQnaPanelMobile ? 'Ask Sinoa your own question' : 'Ask me about this report';
    },

    sinoaIsDelegateClick() {
      return this.$isMobile && this.preGeneratedQna.length > 0 && this.flags['sinoa-content-specific-questions'];
    },
  },

  watch: {
    async rating(newValue) {
      await API.addRating(this.id, newValue);
    },

    async showTeaser(newVal) {
      if (newVal) {
        await this.$nextTick();
        this.applyHTML(this.html);
      }
    },

    open(newVal) {
      if (!newVal) {
        this.showTeaser = false;
        this.partnerCard = null;
        this.$refs.audioInfix.onClose?.();
        this.showQnaPanelMobile = false;
        this.qnaDismissedMobile = false;
        if (document.body) {
          document.body.style.overflow = '';
        }
      }
    },

    recommendedArticles() {
      if (this.showRecommendationCarousel) {
        const parentNode = this.$refs.content;
        this.$refs.recommendationCarousel.applyTo(parentNode);
      }
    },

    async $isDesktop() {
      await this.handleResize(this.$isDesktop);
    },

    async $isTablet() {
      await this.handleResize(this.$isTablet);
    },

    async $isMobile() {
      await this.handleResize(this.$isMobile);
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$refs.wrapper?.addEventListener('scroll', this.handleScroll, { passive: true });
    });
    this.restrictPrint();
    window.previewFile = this.previewFile;
  },

  beforeDestroy() {
    this.$refs.wrapper?.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    ...mapMutations('mediaPlayer', ['setActiveLibrary']),
    ...mapActions('mediaPlayer', ['loadPersistedTime']),
    ...mapActions('recommendations', ['fetchRecommendedArticles']),
    ...mapMutations('recommendations', ['setRecommendedArticles']),
    ...mapMutations('chatbot', ['setGrainFoxAssistantDialogOpen']),

    removeHeaderImage() {
      const header = document.querySelector('.g-header');
      if (header) {
        header.style.backgroundImage = 'none';
        header.style.minHeight = '0px';
        header.style.border = 'none';
      }
    },

    applyAudioInfix() {
      const parentNode = this.$refs.content;
      this.$refs.audioInfix.applyTo(parentNode);
      this.$nextTick(() => {
        this.$refs.audioInfix.onOpen?.();
        this.applyHighlighter();
      });
    },

    applyHighlighter() {
      this.$refs.highlighter.initialize();
      this.$refs.highlighter.updateContainer();
    },

    applyHTML(htmlString) {
      this.$refs.content.innerHTML = htmlString;
      if (this.$isMobile) {
        this.removeHeaderImage();
      }
      this.applyAudioInfix();
    },

    async previewFile(id) {
      this.open = true;
      this.id = id;

      this.isLoading = true;
      this.title = 'Loading...';
      this.$refs.content.innerHTML = '';
      this.setRecommendedArticles([]);

      try {
        const data = await API.previewLibrary(id, this.$isMobile);
        const firstAction = new Date().toISOString();
        if (!this.isExternalUser) {
          this.trackLibraryView(id, firstAction);
        }
        const {
          name,
          datePublished,
          shared,
          rating,
          ratingAllowed,
          showTeaser,
          contentId,
          showTTSTutorial,
          version,
          contentUrl,
        } = data;
        this.shared = shared;
        this.rating = rating;
        this.isRatingEnabled = ratingAllowed;
        this.showTeaser = showTeaser;
        this.contentUrl = contentUrl;
        this.fetchPartnerCard(data.content, data.contentIdentifier);
        this.preGeneratedQna = data.preGeneratedQna || [];
        this.audio_dict = {
          id: this.id,
          name: data.name,
          tagline: data.subheader,
          content: data.content,
          content_image: data.content_image,
          content_image_size: data.content_image_size,
          provider: data.provider,
          showTTSTutorial,
          contentId,
          audio_file_female: data.audio_file_female === 'None' ? null : data.audio_file_female,
          audio_file_male: data.audio_file_male === 'None' ? null : data.audio_file_male,
          showTeaser: this.showTeaser,
          version,
        };
        this.setActiveLibrary(this.audio_dict);
        this.loadPersistedTime();
        this.title = `${name} | ${formatDate(new Date(datePublished))}`;
        this.attachScrollListener(id, firstAction);

        switch (data.type) {
          case 'html': {
            const htmlString = data.html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
            this.html = htmlString;
            this.applyHTML(this.html);
            this.debouncedLibraryViewUpdate();
            break;
          }
          case 'pdf': {
            if (Object.prototype.hasOwnProperty.call(data, 'name')
              && Object.prototype.hasOwnProperty.call(data, 'timestamp')
              && Object.prototype.hasOwnProperty.call(data, 'path')) {
              this.html = data.html;
              this.applyHTML(this.html);

              const hh = await getHH(data.name + data.timestamp);
              this.renderPDF(data.path, hh, this.id);
            }
            break;
          }
          case 'media': {
            const html = this.generateHTMLFromString(data.html);
            html.addEventListener('load', this.debouncedLibraryViewUpdate);
            this.$refs.content.appendChild(html);
            break;
          }
          default: {
            break;
          }
        }
        try {
          if (!this.showTeaser && (this.flags['recommendation-aside'] || this.flags['recommendation-carousel'])) {
            await this.fetchRecommendedArticles(this.contentUrl);
          }
        } catch (e) {
          // Ignore
        }

        const libraryData = readLibraryData();

        if (libraryData?.ask_sinoa_index != null) {
          await this.openDeepLinkedQnaIfNeeded();
        }
      } catch (e) {
        this.$snackbar.error(e.toString());
      } finally {
        this.isLoading = false;
      }
    },

    trackLibraryView(libraryId, firstAction) {
      const trackingData = {
        id: libraryId,
        first_action: firstAction,
        last_action: new Date().toISOString(),
        scroll_depth: 0,
        open: true,
        viewed_at: new Date().toISOString(),
      };

      window.gfa.send('library_view', trackingData);
    },

    renderPDF(path, vk, id) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.5.207/pdf.worker.min.js';
      const loadingTask = window.pdfjsLib.getDocument({ url: path, password: vk });
      document.getElementById('pdf-preview').innerHTML = '';
      loadingTask.promise.then((pdf) => {
        const pages = [];
        const wrapperWidth = this.$refs.wrapper.clientWidth;
        const wrapperHeight = this.$refs.wrapper.clientHeight - 20;

        for (let i = 1; i <= pdf.numPages; i += 1) {
          pages.push(pdf.getPage(i).then((page) => {
            const pdfPreviewElement = document.getElementById('pdf-preview');
            if (this.id !== id || !pdfPreviewElement) {
              return;
            }
            const canvas = document.createElement('canvas');
            const { width, height } = page.getViewport({ scale: 1 });
            const pageWidthScale = wrapperWidth / width;
            const pageHeightScale = wrapperHeight / height;
            const scale = Math.min(pageWidthScale, pageHeightScale);
            const viewport = page.getViewport({ scale });
            const dpr = window.devicePixelRatio || 1;

            canvas.width = Math.floor(viewport.width * dpr);
            canvas.height = Math.floor(viewport.height * dpr);
            canvas.style.maxWidth = `${Math.floor(viewport.width)}px`;
            canvas.style.maxHeight = `${Math.floor(viewport.height)}px`;
            canvas.classList.add('elevation-2');
            pdfPreviewElement?.appendChild(canvas);

            const context = canvas.getContext('2d');
            context.scale(dpr, dpr);
            const renderContext = {
              canvasContext: context,
              viewport,
            };
            page.render(renderContext);
          }));
        }
        Promise.all(pages).then(() => {
          if (typeof this.debouncedLibraryViewUpdate === 'function') {
            this.debouncedLibraryViewUpdate();
          }
        });
      }).catch((e) => {
        datadogLogs.logger.error('File preview PDF error', {}, e);
      });
    },

    restrictPrint() {
      window.addEventListener('beforeprint', () => {
        this.$refs.wrapper.setAttribute('style', 'display:none!important');
      });

      window.addEventListener('afterprint', () => {
        this.$refs.wrapper.setAttribute('style', '');
      });
    },

    contentClickHandler(event) {
      if (event.target == null) {
        return;
      }

      const link = event.target.closest('a');

      if (link) {
        const partnerCtaId = link.getAttribute('data-partner-cta-id');
        if (partnerCtaId) {
          apiRecordPartnerCardCtaClick(parseInt(partnerCtaId, 10)).catch(() => {});
        }

        window.gfa.send('library_click', {
          id: this.id,
          first_action: new Date().toISOString(),
          url: link.href || '',
        });
      }
    },

    attachScrollListener(id, firstAction) {
      this.debouncedLibraryViewUpdate = window.gfa.debounce((event, callback, eventState = true) => {
        if (this.id !== id) {
          return;
        }
        const scrollDepth = this.$refs.wrapper.scrollTop
        / (this.$refs.wrapper.scrollHeight
        - this.$refs.wrapper.clientHeight
        - this.$refs.comments.clientHeight);

        const scrollDepthFormatted = (Number.isNaN(scrollDepth) || Object.is(scrollDepth, -0))
          ? 1 : Number(scrollDepth.toFixed(2));
        const scrollDepthClamped = Math.min(Math.max(scrollDepthFormatted, 0), 1);

        window.gfa.send('library_view', {
          id,
          first_action: firstAction,
          last_action: new Date().toISOString(),
          scroll_depth: scrollDepthClamped,
          open: Boolean(eventState),
          viewed_at: new Date().toISOString(),
        });

        if (callback) {
          callback();
        }
      }, 250);
      this.$refs.wrapper.addEventListener('scroll', this.debouncedLibraryViewUpdate);
    },

    generateHTMLFromString(domString) {
      const p = new DOMParser();
      return p.parseFromString(domString, 'text/html').querySelector('body > *:first-of-type');
    },

    closePreviewDialog() {
      if (this.debouncedLibraryViewUpdate) {
        this.debouncedLibraryViewUpdate(null, () => {
          this.$refs.wrapper.removeEventListener('scroll', this.debouncedLibraryViewUpdate);
          this.debouncedLibraryViewUpdate = null;
          this.$refs.content.innerHTML = '';
        }, false);
      }
      this.partnerCard = null;
      this.showQnaPanelMobile = false;
      if (document.body) {
        document.body.style.overflow = '';
      }
      window.history.replaceState({}, '', '/dashboard/resource_hub/');
      const libraryData = readLibraryData();
      if (libraryData) {
        libraryData.ask_sinoa_index = null;
      }
      this.open = false;
    },

    preventCopy(event) {
      event.preventDefault();
    },

    preventDrag(event) {
      event.preventDefault();
    },

    nextRecommendedArticle() {
      if (this.recommendedResourceHubArticles.length === 0) {
        return;
      }
      const nextArticle = this.recommendedResourceHubArticles[0];
      this.previewFile(nextArticle.id);
    },

    async fetchPartnerCard(contentName, contentIdentifier) {
      this.partnerCard = null;
      const placement = getPlacementForContent(contentName, contentIdentifier);
      if (!placement) return;
      try {
        const cards = await apiGetPartnerCardsByPlacement(placement, this.userLocationCode);
        this.partnerCard = cards.length > 0 ? cards[0] : null;
        if (this.partnerCard) {
          apiRecordPartnerCardImpression(this.partnerCard.id, placement).catch(() => {});
        }
      } catch {
        this.partnerCard = null;
      }
    },

    handlePartnerCtaClick(cta) {
      if (cta.id) {
        apiRecordPartnerCardCtaClick(cta.id).catch(() => {});
      }
    },

    async handleResize(viewport) {
      if (viewport && this.open) {
        await this.previewFile(this.id);
      }
    },

    handleScroll(e) {
      if (this.isGrainFoxAssistantDialogOpen) {
        this.scrollDetected = false;
        return;
      }

      const target = e?.target || this.$refs.wrapper;
      if (!target) return;

      const currentScrollTop = target.scrollTop;
      if (currentScrollTop < this.lastScrollTop) {
        this.scrollDetected = false;
      } else if (currentScrollTop > this.lastScrollTop) {
        this.scrollDetected = true;
      }
      this.lastScrollTop = Math.max(currentScrollTop, 0);
    },

    handleFabClick() {
      if (this.$isMobile) {
        if (this.preGeneratedQna.length > 0) {
          if (this.qnaDismissedMobile) {
            this.setGrainFoxAssistantDialogOpen(true);
            return;
          }
          if (!this.showQnaPanelMobile) {
            this.showQnaPanelMobile = true;
            if (document.body) {
              document.body.style.overflow = 'hidden';
            }
            return;
          }
          this.setGrainFoxAssistantDialogOpen(true);
          return;
        }
        this.setGrainFoxAssistantDialogOpen(true);
        return;
      }
      this.setGrainFoxAssistantDialogOpen(true);
    },

    handleCloseMobilePanel() {
      this.showQnaPanelMobile = false;
      this.qnaDismissedMobile = true;
      if (document.body) {
        document.body.style.overflow = '';
      }
    },

    async openDeepLinkedQnaIfNeeded() {
      const libraryData = readLibraryData();
      const askIndex = Number(libraryData?.ask_sinoa_index);
      if (Number.isNaN(askIndex)) return;

      datadogRum.addAction('qna-open-from-email', {
        libraryId: this.id,
        askIndex,
      });

      await this.$nextTick();

      const qna = this.preGeneratedQna[askIndex - 1];
      if (!qna) return;

      this.setGrainFoxAssistantDialogOpen(true);
      this.showQnaPanelMobile = true;

      /* eslint-disable no-await-in-loop, no-promise-executor-return */
      while (!this.$refs.articleQnaChips) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        await this.$nextTick();
      }

      /* eslint-disable no-await-in-loop, no-promise-executor-return */
      while (this.isChatUsageLoading) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.$refs.articleQnaChips.handleQnaClick(qna);
    },
  },
};
</script>

<style lang="scss">
.preview-card {
  height: 100%;
  overflow: hidden;

  &__wrapper {
    height: 100%;
    overflow-y: scroll;
  }

  &__content {
    all: initial;
    user-select: none;
  }
}

.article-qna-chips-desktop {
  position: fixed;
  bottom: 16px;
  right: 32px;
  width: 320px;
  max-width: 25vw;
  z-index: 10;
}

.preview-card__partner-drawer {
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 6;
  border-radius: 0 12px 12px 0 !important;
  border: 1px solid #F47721 !important;
  border-left: none !important;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08) !important;
}

.article-qna-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 80;
  pointer-events: auto;
  touch-action: none;
  overscroll-behavior: none;
}
</style>
